import type { Node } from "@xyflow/react";
import type { OrganizationAction, OrganizedNode } from "../types/board";
import type { CanvasGraph, GraphCommand } from "./canvas-commands";
import { expandGroupSelection, groupId, hasParentTopology, normalizeGroups, rankedNodes, stackUnits } from "./canvas-groups";
import { NODE_DEFAULT_SIZES, type BoardNodeType } from "./node-creation";
import { getNodeCapabilities } from "./node-formatting";

export type OrganizationBox = { x: number; y: number; width: number; height: number };
export type OrganizationObject = { nodes: Node[]; box: OrganizationBox };
export type OrganizationAvailability = Record<OrganizationAction, { enabled: boolean; reason?: string }>;
export const ORGANIZATION_LABELS: Record<OrganizationAction, string> = {
  group: "Group", ungroup: "Ungroup", left: "Align left", center: "Align center", right: "Align right",
  top: "Align top", middle: "Align middle", bottom: "Align bottom", horizontal: "Distribute horizontally",
  vertical: "Distribute vertically", forward: "Bring forward", backward: "Send backward", front: "Bring to front", back: "Send to back",
};

/** The page supplies current measurements; all remaining operands are plain authored values. */
export function nodeOrganizationBox(node: Node, measured = node.measured): OrganizationBox | null {
  const defaults = NODE_DEFAULT_SIZES[node.type as BoardNodeType];
  if (!getNodeCapabilities(node.type) || node.hidden || node.selectable === false || node.draggable === false ||
    hasParentTopology(node) || node.extent != null) return null;
  const size = (key: "width" | "height") => measured?.[key] ?? node[key] ?? node.style?.[key] ?? defaults?.[key];
  const width = size("width"), height = size("height"), origin = node.origin ?? [0, 0];
  if (typeof width !== "number" || typeof height !== "number" || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 ||
    origin.length !== 2 || origin.some(value => !Number.isFinite(value) || value < 0 || value > 1)) return null;
  const x = node.position.x - origin[0] * width, y = node.position.y - origin[1] * height;
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y, width, height } : null;
}

export function organizationBoxes(nodes: Node[]): Map<string, OrganizationBox> {
  return new Map(nodes.flatMap(node => { const box = nodeOrganizationBox(node); return box ? [[node.id, box] as const] : []; }));
}

function union(boxes: OrganizationBox[]): OrganizationBox {
  const x = Math.min(...boxes.map(box => box.x)), y = Math.min(...boxes.map(box => box.y));
  return { x, y, width: Math.max(...boxes.map(box => box.x + box.width)) - x,
    height: Math.max(...boxes.map(box => box.y + box.height)) - y };
}

export function selectedObjects(graph: CanvasGraph, boxes: Map<string, OrganizationBox>): OrganizationObject[] | null {
  const ids = expandGroupSelection(graph.nodes, graph.nodes.filter(node => node.selected).map(node => node.id));
  const units = stackUnits(graph.nodes).filter(unit => unit.some(node => ids.has(node.id)));
  if (units.some(unit => unit.some(node => !boxes.has(node.id)))) return null;
  return units.map(nodes => ({ nodes, box: union(nodes.map(node => boxes.get(node.id)!)) }));
}

function reordered(units: Node[][], selected: Set<string>, action: OrganizationAction): Node[][] {
  const chosen = (unit: Node[]) => unit.some(node => selected.has(node.id));
  if (action === "front") return [...units.filter(unit => !chosen(unit)), ...units.filter(chosen)];
  if (action === "back") return [...units.filter(chosen), ...units.filter(unit => !chosen(unit))];
  const result = [...units];
  if (action === "forward") {
    for (let i = result.length - 2; i >= 0; i--) if (chosen(result[i]) && !chosen(result[i + 1])) [result[i], result[i + 1]] = [result[i + 1], result[i]];
  } else if (action === "backward") {
    for (let i = 1; i < result.length; i++) if (chosen(result[i]) && !chosen(result[i - 1])) [result[i], result[i - 1]] = [result[i - 1], result[i]];
  }
  return result;
}
const isStack = (action: OrganizationAction) => ["forward", "backward", "front", "back"].includes(action);

export function organizationState(graph: CanvasGraph, boxes: Map<string, OrganizationBox>, disabledReason?: string): {
  actions: OrganizationAvailability; selectionSummary: string;
} {
  const ids = expandGroupSelection(graph.nodes, graph.nodes.filter(node => node.selected).map(node => node.id));
  const selected = graph.nodes.filter(node => ids.has(node.id));
  const groups = new Set(selected.map(groupId).filter(id => id !== undefined));
  const objects = selectedObjects(graph, boxes), count = objects?.length ?? 0;
  const units = stackUnits(graph.nodes);
  const mixed = graph.edges.some(edge => edge.selected);
  const actions = Object.fromEntries((Object.keys(ORGANIZATION_LABELS) as OrganizationAction[]).map(action => {
    let reason = disabledReason || (!selected.length ? "Select node objects; connectors are excluded." :
      !objects ? "Selection contains a restricted, unsupported or unmeasured object." : undefined);
    if (!reason && (action === "group" || isStack(action)) && graph.nodes.some(hasParentTopology)) reason = "Grouping and stacking require a flat diagram without parent containers.";
    if (!reason && action === "ungroup" && !groups.size) reason = "Select at least one group. Free nodes stay unchanged.";
    if (!reason && ["group", "left", "center", "right", "top", "middle", "bottom"].includes(action) && count < 2) reason = "Select at least two eligible objects; each group counts as one.";
    if (!reason && ["horizontal", "vertical"].includes(action) && count < 3) reason = "Select at least three eligible objects; each group counts as one.";
    if (!reason && isStack(action) && reordered(units, ids, action).every((unit, index) => unit === units[index])) reason = "Selection is already at this stack boundary.";
    return [action, { enabled: !reason, ...(reason ? { reason } : {}) }];
  })) as OrganizationAvailability;
  return { actions, selectionSummary: `${groups.size} group${groups.size === 1 ? "" : "s"}, ${selected.filter(node => groupId(node) === undefined).length} free nodes selected. Groups count as one object. Ungroup leaves free nodes unchanged.${mixed ? " Connectors are excluded from organization." : ""}` };
}

export function organize(action: OrganizationAction, boxes: Map<string, OrganizationBox>, newGroupId?: string): GraphCommand {
  return graph => {
    if (!organizationState(graph, boxes).actions[action].enabled) return graph;
    const objects = selectedObjects(graph, boxes)!;
    const ids = new Set(objects.flatMap(object => object.nodes.map(node => node.id)));
    if (action === "group") {
      if (!newGroupId || [...graph.nodes, ...graph.edges].some(item => item.id === newGroupId) || graph.nodes.some(node => groupId(node) === newGroupId)) return graph;
      return normalizeGroups({ ...graph, nodes: graph.nodes.map(node => ids.has(node.id) ? { ...node, group_id: newGroupId } : node) });
    }
    if (action === "ungroup") return { ...graph, nodes: graph.nodes.map(node => {
      if (!ids.has(node.id)) return node;
      const next: OrganizedNode = { ...node }; delete next.group_id; return next;
    }) };
    if (isStack(action)) return { ...graph, nodes: rankedNodes(reordered(stackUnits(graph.nodes), ids, action).flat()) };

    const offsets = new Map<string, { x: number; y: number }>();
    const move = (object: OrganizationObject, x: number, y: number) => {
      for (const node of object.nodes) offsets.set(node.id, { x, y });
    };
    if (action === "horizontal" || action === "vertical") {
      const axis = action === "horizontal" ? "x" : "y", size = action === "horizontal" ? "width" : "height";
      const ordered = objects.map((object, index) => ({ object, index })).sort((a, b) => a.object.box[axis] - b.object.box[axis] || a.index - b.index).map(item => item.object);
      const first = ordered[0].box, last = ordered[ordered.length - 1].box;
      const gap = (last[axis] + last[size] - first[axis] - ordered.reduce((sum, object) => sum + object.box[size], 0)) / (ordered.length - 1);
      let cursor = first[axis] + first[size] + gap;
      for (const object of ordered.slice(1, -1)) {
        const delta = cursor - object.box[axis]; move(object, axis === "x" ? delta : 0, axis === "y" ? delta : 0);
        cursor += object.box[size] + gap;
      }
    } else {
      const bounds = union(objects.map(object => object.box));
      for (const object of objects) {
        const box = object.box;
        const x = action === "left" ? bounds.x - box.x : action === "center" ? bounds.x + bounds.width / 2 - box.x - box.width / 2 : action === "right" ? bounds.x + bounds.width - box.x - box.width : 0;
        const y = action === "top" ? bounds.y - box.y : action === "middle" ? bounds.y + bounds.height / 2 - box.y - box.height / 2 : action === "bottom" ? bounds.y + bounds.height - box.y - box.height : 0;
        move(object, x, y);
      }
    }
    if ([...offsets.values()].some(delta => !Number.isFinite(delta.x) || !Number.isFinite(delta.y))) return graph;
    const nodes = graph.nodes.map(node => { const delta = offsets.get(node.id); return delta && (delta.x || delta.y) ? { ...node,
      position: { ...node.position, x: node.position.x + delta.x, y: node.position.y + delta.y } } : node; });
    return nodes.some(node => !Number.isFinite(node.position.x) || !Number.isFinite(node.position.y)) ? graph : { ...graph, nodes };
  };
}

/** One delta per unit per framework batch; resizing stays an individual edit at the caller. */
export function translateGroupChanges(graph: CanvasGraph, positions: { id: string; position: { x: number; y: number } }[], boxes: Map<string, OrganizationBox>): Node[] {
  const deltas = new Map<string, { x: number; y: number }>();
  const grouped = new Map<string, Node[]>();
  for (const node of graph.nodes) { const id = groupId(node); if (id !== undefined) grouped.set(id, [...(grouped.get(id) ?? []), node]); }
  const direct = new Map(positions.map(change => [change.id, change.position]));
  for (const change of positions) {
    const node = graph.nodes.find(node => node.id === change.id), id = node && groupId(node);
    if (node && id !== undefined && !deltas.has(id) && grouped.get(id)!.every(member => boxes.has(member.id)))
      deltas.set(id, { x: change.position.x - node.position.x, y: change.position.y - node.position.y });
  }
  return graph.nodes.map(node => {
    const id = groupId(node), delta = id !== undefined ? deltas.get(id) : undefined;
    const position = id === undefined ? direct.get(node.id) : delta ? { ...node.position, x: node.position.x + delta.x, y: node.position.y + delta.y } : undefined;
    return position && Number.isFinite(position.x) && Number.isFinite(position.y) ? { ...node, position } : node;
  });
}
